/**
 * Chat Gate Component
 *
 * Conditional wrapper that controls access to chat functionality.
 * Displays appropriate prompts for authentication and payment requirements,
 * or renders the actual chat interface when conditions are met.
 *
 * IMPORTANT: This web app requires the Chrome extension to be installed!
 * Payment detection relies on the extension monitoring Stripe checkout pages.
 */

import React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:chat-gate');
import { useCreateCheckoutSessionMutation } from '../hooks/use-gateway-queries';
import { usePlatform } from '../contexts/platform-context';
import { useAuth } from '../services/auth-service';
import { useChatAvailability } from '../services/chat-availability-service';
import { useExtensionDetection } from '../hooks/use-extension-detection';
import { cn } from '../utils/cn';
import { logError } from '../utils/safe-logger';
import { toast } from '../utils/toast';
import { AgreementPrompt } from './agreement-prompt';
import { EmbeddedPricingTable } from './embedded-pricing-table';
import { ExtensionRequiredPrompt } from './extension-required-prompt';
import { PaymentPrompt } from './payment-prompt';
import { SetupRequiredPrompt } from './setup-required-prompt';
import { SignInPrompt } from './sign-in-prompt';

interface ChatGateProps {
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
  // Custom loading component
  loadingComponent?: React.ReactNode;
  // Custom fallback components
  signInComponent?: React.ReactNode;
  paymentComponent?: React.ReactNode;
  agreementComponent?: React.ReactNode;
}

export const ChatGate: React.FC<ChatGateProps> = ({
  children,
  className,
  onClose,
  loadingComponent,
  signInComponent,
  paymentComponent,
  agreementComponent,
}) => {
  const { availability, refreshAvailability } = useChatAvailability();
  const { user } = useAuth();
  const { isSidebar, isWeb } = usePlatform();
  const { hasExtension, isDetecting: isDetectingExtension, isMobile } = useExtensionDetection();

  // Listen for extension success notifications (from stripe-monitor content script)
  React.useEffect(() => {
    logger.info('🎧 [CHAT-GATE] Setting up message listener for extension...');

    const handleExtensionSuccess = (event: MessageEvent) => {
      logger.info('📨 [CHAT-GATE] Received window message:', {
        type: event.data?.type,
        source: event.source === window ? 'window' : 'other',
      });

      // Only accept messages from our window
      if (event.source !== window) {
        logger.info('⚠️ [CHAT-GATE] Ignoring message from external source');
        return;
      }

      const message = event.data;

      // Test message from content script
      if (message.type === 'EXTENSION_TEST') {
        logger.info('✅ [CHAT-GATE] Received test message from extension!', message);
        return;
      }

      // PandaDoc BAA signing success
      if (message.type === 'PANDADOC_SUCCESS') {
        logger.info('🎉 [CHAT-GATE] BAA signing success from extension!', {
          sessionId: message.sessionId,
        });

        // Show success toast
        toast.success(
          'BAA Signed! 🎉',
          'Your Business Associate Agreement has been signed. Access will be granted shortly!'
        );

        // Trigger immediate refetch of chat availability
        logger.info('🔄 [CHAT-GATE] Triggering chat availability refresh...');
        refreshAvailability();
        return;
      }

      if (message.type === 'STRIPE_CHECKOUT_SUCCESS') {
        logger.info('🎉 [CHAT-GATE] Payment success from extension!', {
          sessionId: message.sessionId,
        });

        // Show success toast
        toast.success(
          'Payment Successful! 💳',
          "Your subscription is being activated. You'll have access in a moment!"
        );

        // Hide pricing table
        setShowPricingTable(false);
        setCustomerSessionClientSecret(null);
        setSubscriptionError(null);

        // Trigger immediate refetch of chat availability
        logger.info('🔄 [CHAT-GATE] Triggering chat availability refresh...');
        refreshAvailability();
      }
    };

    window.addEventListener('message', handleExtensionSuccess);

    return () => {
      window.removeEventListener('message', handleExtensionSuccess);
    };
  }, [refreshAvailability]);

  // IMPORTANT: All useState hooks must be at the top level to comply with React's Rules of Hooks
  // This fixes the error: "React has detected a change in the order of Hooks called by ChatGate"
  // Hooks cannot be called conditionally, so we declare all state variables here regardless of which ones are used
  const [isSigningIn, setIsSigningIn] = React.useState(false);
  const [isSubscribing, setIsSubscribing] = React.useState(false);
  const [showPricingTable, setShowPricingTable] = React.useState(false);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = React.useState<
    string | null
  >(null);
  const [subscriptionError, setSubscriptionError] = React.useState<string | null>(null);

  // Track mounted state to prevent state updates after unmount
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Create checkout session mutation for getting client secret
  const createCheckoutSessionMutation = useCreateCheckoutSessionMutation();

  // Listen for extension success notifications (EXTENSION REQUIRED)
  // The Chrome extension monitors Stripe checkout page DOM and notifies us when payment completes
  React.useEffect(() => {
    const handleExtensionSuccess = (event: MessageEvent) => {
      // Only accept messages from our window
      if (event.source !== window) return;

      const message = event.data;
      if (message.type === 'STRIPE_CHECKOUT_SUCCESS') {
        logger.info('🎉 [CHAT-GATE] Payment success from extension!', {
          sessionId: message.sessionId,
        });

        // Show success toast
        toast.success(
          'Payment Successful! 💳',
          "Your subscription is being activated. You'll have access in a moment!"
        );

        // Hide pricing table
        setShowPricingTable(false);
        setCustomerSessionClientSecret(null);
        setSubscriptionError(null);

        // The chat availability service will refresh automatically via background polling
      }
    };

    window.addEventListener('message', handleExtensionSuccess);

    return () => {
      window.removeEventListener('message', handleExtensionSuccess);
    };
  }, []);

  // Loading state
  if (availability.status === 'loading') {
    if (loadingComponent) {
      return <div className={className}>{loadingComponent}</div>;
    }

    return (
      <div
        className={cn(
          'bg-background flex items-center justify-center h-full min-h-96 p-6',
          className
        )}
      >
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Authentication required
  if (availability.status === 'requires_auth') {
    if (signInComponent) {
      return <div className={className}>{signInComponent}</div>;
    }

    // Wrap the onAction function to manage loading state
    const handleSignIn = async () => {
      if (!availability.onAction) {
        logger.error('❌ [CHAT-GATE] onAction not available', {
          status: availability.status,
          isEnabled: availability.isEnabled,
          message: availability.message,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      try {
        logger.info('[CHAT-GATE] button clicked', performance.now());
        setIsSigningIn(true);
        logger.info('[CHAT-GATE] state set, before yield', performance.now());

        // Yield to React's render cycle to ensure loading state is visible
        // This prevents "async starvation" where the UI doesn't update before
        // the long-running network call (loginQuery.refetch) blocks the thread
        await new Promise(resolve => setTimeout(resolve, 0));

        logger.info('[CHAT-GATE] after yield, before action', performance.now());
        await availability.onAction();
        logger.info('[CHAT-GATE] action completed', performance.now());

        // Success: Auth flow initiated, external window opened
        // Keep loading state for a brief moment to show feedback, then reset
        // The auth continues in an external window, so we can't track completion
        setTimeout(() => {
          if (isMountedRef.current) {
            setIsSigningIn(false);
          }
        }, 1500); // Brief feedback period
      } catch (error) {
        logger.error('❌ [CHAT-GATE] Sign in failed:', error);
        // Error: Reset loading state immediately
        if (isMountedRef.current) {
          setIsSigningIn(false);
        }
      }
    };

    return (
      <div className={`${className} bg-background`}>
        <SignInPrompt
          onSignIn={handleSignIn}
          {...(onClose && { onClose })}
          isLoading={isSigningIn || !availability.onAction}
        />
      </div>
    );
  }

  // Manual (invoice) billing paused by Enter staff — full-page notice, no Stripe / self-serve.
  if (availability.status === 'billing_suspended') {
    return (
      <div
        className={cn('bg-background flex items-center justify-center h-full min-h-96 p-6', className)}
      >
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-foreground mb-2">Account paused</h1>
          <p className="text-muted-foreground text-sm">
            {availability.message ||
              'Your account is paused. Please contact your Enter representative to restore access.'}
          </p>
        </div>
      </div>
    );
  }

  // Extension required gate — only on web (not sidebar/desktop), only when setup is still needed.
  // Once the user has subscription + BAA they can use any device without the extension.
  const needsSetup =
    availability.status === 'requires_agreement' || availability.status === 'requires_payment';

  // While waiting for the extension to respond, show a neutral loading screen so
  // there is no flash of the payment/BAA UI before we know whether the extension is present.
  if (isWeb && needsSetup && isDetectingExtension) {
    return (
      <div
        className={cn(
          'bg-background flex items-center justify-center h-full min-h-96 p-6',
          className
        )}
      >
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isWeb && needsSetup && !isDetectingExtension && !hasExtension) {
    const reason =
      availability.status === 'requires_agreement' &&
      availability.requirements?.includes('requires_payment')
        ? 'both'
        : availability.status === 'requires_agreement'
          ? 'baa'
          : 'subscription';

    return (
      <div className={`${className} bg-background`}>
        <ExtensionRequiredPrompt reason={reason} isMobile={isMobile} />
      </div>
    );
  }

  // In sidebar: Show unified setup prompt for ANY requirements (BAA or payment)
  if (
    isSidebar &&
    (availability.status === 'requires_agreement' || availability.status === 'requires_payment')
  ) {
    // Use requirements array from availability state (contains all missing requirements)
    const requirements = availability.requirements || [];

    logger.info('🔍 [CHAT-GATE] Sidebar detected with requirements:', requirements);

    return (
      <div className={`${className} bg-background`}>
        <SetupRequiredPrompt requirements={requirements} />
      </div>
    );
  }

  // Agreement required (for admins before payment) - Full web app only
  if (availability.status === 'requires_agreement') {
    if (agreementComponent) {
      return <div className={className}>{agreementComponent}</div>;
    }

    return (
      <div className={`${className} bg-background`}>
        <AgreementPrompt {...(onClose && { onClose })} />
      </div>
    );
  }

  // Payment required - Full web app only
  if (availability.status === 'requires_payment') {
    if (paymentComponent) {
      return <div className={className}>{paymentComponent}</div>;
    }

    // Show embedded pricing table if we have the client secret
    if (showPricingTable && customerSessionClientSecret) {
      const handleSubscriptionSuccess = () => {
        logger.info('🎉 [CHAT-GATE] Subscription completed successfully');
        setShowPricingTable(false);
        setCustomerSessionClientSecret(null);
        setSubscriptionError(null);
        // The EmbeddedPricingTable already refreshes auth
      };

      const handlePricingTableError = (error: string) => {
        logger.error('❌ [CHAT-GATE] Pricing table error:', error);
        setSubscriptionError(error);
        setShowPricingTable(false);
        setCustomerSessionClientSecret(null);
      };

      return (
        <div className={`${className} bg-background h-full overflow-y-auto`}>
          <div className="h-full">
            <EmbeddedPricingTable
              customerSessionClientSecret={customerSessionClientSecret}
              onSuccess={handleSubscriptionSuccess}
              onError={handlePricingTableError}
            />
          </div>
        </div>
      );
    }

    // Show payment prompt with Subscribe button
    const handleSubscribe = async () => {
      if (!user) {
        setSubscriptionError('User not authenticated');
        return;
      }

      try {
        setIsSubscribing(true);
        setSubscriptionError(null);
        logger.info('🔄 [CHAT-GATE] Getting customer session for embedded pricing table');

        // Call the API to get the customer session client secret
        const response = await createCheckoutSessionMutation.mutateAsync({});

        if (response.status === 200 && response.body.customerSessionClientSecret) {
          logger.info(
            '✅ [CHAT-GATE] Got customer session client secret, showing embedded pricing table'
          );
          setCustomerSessionClientSecret(response.body.customerSessionClientSecret);
          setShowPricingTable(true);
        } else {
          logger.error('❌ [CHAT-GATE] Failed to get client secret:', response);
          setSubscriptionError('Failed to load pricing table. Please try again later.');
        }
      } catch (error) {
        logger.error('❌ [CHAT-GATE] Error loading pricing table:', error);
        setSubscriptionError(error instanceof Error ? error.message : 'Unknown error');
        logError('CHAT-GATE Error loading pricing table', error);
      } finally {
        setIsSubscribing(false);
      }
    };

    return (
      <div className={`${className} bg-background`}>
        {subscriptionError && (
          <div className="p-4 mb-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 text-red-900 dark:text-red-200 rounded-md">
            <p>{subscriptionError}</p>
          </div>
        )}
        <PaymentPrompt
          onAddPayment={handleSubscribe}
          {...(onClose && { onClose })}
          isLoading={isSubscribing}
        />
      </div>
    );
  }

  // // Insufficient seats warning for admins
  // if (availability.status === 'insufficient_seats') {
  // if (paymentComponent) {
  // return <div className={className}>{paymentComponent}</div>;
  // }
  //
  // // Add loading state for payment process
  // const [isSubscribing, setIsSubscribing] = React.useState(false);
  //
  // // Wrap the onAction function to manage loading state
  // const handleSubscribe = async () => {
  // if (!availability.onAction) return;
  //
  // try {
  // setIsSubscribing(true);
  // await availability.onAction();
  // } catch (error) {
  // logger.error('Subscription update failed:', error);
  // } finally {
  // // Set a timeout to reset the loading state after a reasonable time
  // // This is needed because the payment flow continues in an external window
  // setTimeout(() => {
  // setIsSubscribing(false);
  // }, 3000); // Reset after 3 seconds
  // }
  // };
  //
  // return (
  // <div className={`${className} bg-background`}>
  // <PaymentPrompt
  // message={availability.message}
  // actionLabel={availability.actionLabel}
  // onAddPayment={handleSubscribe}
  // isLoading={isSubscribing}
  // isWarning={true}
  // />
  // </div>
  // );
  // }

  // Not a member - show access denied message
  if (availability.status === 'not_a_member') {
    return (
      <div
        className={cn(
          'bg-background flex items-center justify-center h-full min-h-96 p-6',
          className
        )}
      >
        <div className="w-full max-w-md text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 14.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Access Not Available</h2>
          <p className="text-muted-foreground leading-relaxed">{availability.message}</p>
        </div>
      </div>
    );
  }

  // Chat is available - render children
  if (availability.status === 'available') {
    return <div className={className}>{children}</div>;
  }

  // Fallback for unexpected states
  return (
    <div className={cn('flex items-center justify-center h-full min-h-96 p-6', className)}>
      <div className="text-center">
        <p className="text-muted-foreground">
          Chat is temporarily unavailable. Please try again later.
        </p>
      </div>
    </div>
  );
};

ChatGate.displayName = 'ChatGate';
